import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Affiche les prix multi-canaux d'un produit avec leur marge calculée (sur le cost_price).
 * Extrait de Products.jsx — iter22 modularisation.
 *
 * Props:
 * - product: { cost_price, price_direct, price_distributor, price_online }
 * - minMarginPct (number, default 30): seuil d'alerte. Marge < seuil → rouge + ⚠️.
 *
 * Code couleur marge :
 *   ≥ 50% : vert (sain)
 *   ≥ seuil min : ambre (acceptable)
 *   < seuil min : rouge + icône warning (à surveiller)
 */
export default function PriceMarginCell({ product, minMarginPct = 30 }) {
  const cost = Number(product.cost_price);
  const channels = [
    { key: "price_direct", label: "D", title: "Vente directe" },
    { key: "price_distributor", label: "B", title: "B2B distributeur" },
    { key: "price_online", label: "I", title: "Internet" },
  ];
  const hasAny = channels.some((c) => product[c.key] != null && product[c.key] !== "");
  if (!hasAny) {
    return <span className="text-[#A3A6A3] text-xs italic font-mono">—</span>;
  }
  return (
    <div className="inline-flex flex-col items-end gap-0.5 text-xs">
      {channels.map((c) => {
        const price = Number(product[c.key]);
        if (!product[c.key]) return null;
        const showMargin = !!cost && cost > 0 && price > cost;
        const marginPct = showMargin ? Math.round(((price - cost) / price) * 100) : null;
        const isLow = marginPct != null && marginPct < minMarginPct;
        const marginColor =
          marginPct == null ? "text-[#A3A6A3]" :
          isLow ? "text-[#C05C47]" :
          marginPct >= 50 ? "text-[#1F4E3D]" :
          "text-[#8C6B2F]";
        return (
          <div key={c.key} className="flex items-center gap-1.5 font-mono" title={c.title}>
            <span className="text-[10px] uppercase tracking-wider text-[#575A57] w-3">{c.label}</span>
            <span className="text-[#1A1C1A] font-semibold">{price.toFixed(2)}</span>
            {marginPct != null && (
              <span
                className={`${marginColor} text-[10px] font-semibold flex items-center gap-0.5`}
                title={isLow ? `Marge sous le seuil minimum (${minMarginPct}%)` : undefined}
                data-testid={isLow ? `margin-cell-low-${c.key}` : undefined}
              >
                {isLow && <AlertTriangle className="w-3 h-3" />}
                +{marginPct}%
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
